import type { EngineFileNode } from './types'
/**
 * Yjs-record → EngineNode converters.
 *
 * Centralises the per-kind builder functions that turn a Yjs store entry
 * into the engine's scene-graph shape. Each function covers exactly one
 * `EngineNodeKind` input path and is referenced from `NodeKindRegistry`
 * so adding a new kind means writing a new converter + registering it.
 *
 * Note: shot / image / audio / set can each come from either a completed
 * `Asset` or a pending `Job`, so those kinds have two builders.
 */

import { isInterruptedUpload } from '@/lib/interruptedUpload'
import type { Asset, Job, CanvasBin, TextAnnotation, SubjectCollection } from '@/types'
import type { ShotColorLabel } from '@/types'
import type { BinPreviewItem } from './types'
import type { Action, WorkflowRunStatus } from '@/types/shared'
import type { CanvasPosition } from '@/store/yjsStore'
import { isPlaceholderSubjectId } from '@/components/canvas/shared/createPlaceholderSubjectNodes'
import type {
  EngineMetadataBadge,
  EngineShotNode,
  EngineImageNode,
  EngineAudioNode,
  EngineSetNode,
  EngineBinGroupNode,
  EngineTextAnnotationNode,
  EngineSubjectNode,
  EngineSubjectCollectionNode,
  EngineVariableNode,
  EngineActionNode,
} from './types'
import {
  AUDIO_NODE_WIDTH,
  AUDIO_CONTENT_HEIGHT,
  SET_NODE_WIDTH,
  SET_THUMBNAIL_HEIGHT,
  calculateNodeDimensions,
  parseAspectRatio,
  clampAspectRatio,
  DEFAULT_ASPECT_RATIO,
} from '@/components/canvas/shared/canvasConstants'
import { STANDARD_RESOLUTIONS } from '@/components/upscale/resolutions'
import { DEFAULT_BIN_WIDTH, DEFAULT_BIN_HEIGHT } from '@/components/canvas/shared/canvasPanelConstants'
import { TEXT_ANNOTATION_MIN_HEIGHT } from './nodes/shared-constants'
import { getStorageUrl } from '@/lib/api'
import { calculateV9DisplayProgress } from '@/lib/v9ShotDisplayProgress'
import { calculateProgress } from '@/lib/generationTiming'
import { resolveCollectionLayout } from '@/lib/subjectCollectionCardLayout'
import type { SubjectCanvasNode, VariableCanvasNode, StillImageThumbnailTier } from '@/types'
import {
  canSourceProvideHighTier,
  getStillImagePreviewTier,
  getStillImagePreviewUri,
  hasStillImageThumbnailMetadata,
  isHighStillImageTier,
} from '@/lib/stillImagePreview'
import { getCameraMotionFinalFrameUri, isImageAsset } from '@/lib/assetUtils'
import { getAssetMediaKind } from '@/lib/assetMediaKind'
import { assetIsHdr } from '@/lib/proxySpecs'
import { buildActionStatus, mirrorStands } from '@/lib/workflowRunPresentation'

function isV9Rendering(asset: Asset): boolean {
  // A final URI is the strongest signal that the render completed. Phase and
  // operation fields can lag in Yjs/WS state, so apply that guard before any
  // phase-based rendering decision.
  if (asset.uri || asset.v9ShotFailed || asset.v9ShotPhase === 'captured' || asset.v9ShotPhase === 'failed') {
    return false
  }
  return asset.v9ShotPhase === 'rendering' || !!asset.v9ShotPhase || !!asset.v9ShotOperationId
}

function getV9DisplayProgress(asset: Asset): number | undefined {
  const pct = calculateV9DisplayProgress({
    operationId: asset.v9ShotOperationId,
    failed: asset.v9ShotFailed ?? false,
    progress: asset.v9ShotProgress,
    phase: asset.v9ShotPhase,
    phaseStartedAt: asset.v9ShotPhaseStartedAt,
  })
  return pct ?? undefined
}

function parseTimestampMs(value: string | null | undefined): number | undefined {
  if (!value) return undefined
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? undefined : ms
}

function getV9GenerationStartedAt(asset: Asset): number | undefined {
  return parseTimestampMs(asset.v9ShotStartedAt) ?? parseTimestampMs(asset.v9ShotPhaseStartedAt)
}

const CANVAS_RESOLUTION_BADGES = new Set(['2K', 'UHD', '4K', '8K'])

function getAssetResolutionBadge(asset: Asset): EngineMetadataBadge | undefined {
  if (!asset.width || !asset.height) return undefined
  const label = STANDARD_RESOLUTIONS[`${asset.width}x${asset.height}`]
  if (!label || !CANVAS_RESOLUTION_BADGES.has(label)) return undefined
  return { text: label, tone: 'neutral' }
}

function getAssetHdrBadges(asset: Asset): EngineMetadataBadge[] {
  // assetIsHdr is the shared determination used by the spec line and modal too,
  // so all three surfaces label the same asset consistently.
  if (!assetIsHdr(asset)) return []

  // Just "HDR" on the pill — "HDR10/SDR" read like the HDR10 consumer-TV standard
  // and the slash was ambiguous. The bit depth lives in the expanded-view spec
  // line (e.g. "10-bit HDR (PQ)") where there's room; the canvas pill carries the
  // one word a filmmaker recognizes, with the master/preview nuance in the tooltip.
  // Monochrome like every other badge — no gold (matches the app's UI).
  return [
    {
      text: 'HDR',
      tone: 'neutral',
      tooltip: asset.toneMapFallback ? 'hdr source · sdr preview, approx. color' : 'hdr source · sdr preview',
    },
  ]
}

function getFlux3DraftBadge(asset: Asset): EngineMetadataBadge | undefined {
  if (asset.seedanceRenderQuality === 'draft')
    return {
      text: 'Seedance Draft',
      tone: 'neutral',
      tooltip: asset.enhanceUntil
        ? `Enhance until ${new Date(asset.enhanceUntil).toLocaleString()}`
        : 'Enhancement unavailable',
      action: asset.enhanceUntil && Date.parse(asset.enhanceUntil) > Date.now() ? 'flux3-enhance' : undefined,
    }
  if (asset.flux3RenderQuality !== 'draft') return undefined
  // "FLUX Draft", never bare "Draft": on the canvas that word already means an
  // unfired node, and this badge marks the opposite — a completed preview render.
  return {
    text: 'FLUX Draft',
    tone: 'neutral',
    tooltip: asset.flux3DraftEnhanceEligible ? 'click to enhance this take to full quality' : 'draft quality',
    action: asset.flux3DraftEnhanceEligible ? 'flux3-enhance' : undefined,
  }
}

/**
 * Continuations looked like unrelated takes: the chain relationship only
 * surfaced after opening Shot Details. The badge names the relationship at
 * the canvas. Plain "Extension" (no index) — computing the chain position
 * needs the full asset map, which this converter deliberately does not get,
 * and the ordinal is one click away in the detail panel's section chips.
 */
function getExtensionBadge(asset: Asset): EngineMetadataBadge | undefined {
  if (asset.operation !== 'extend' || (asset.status !== 'completed' && !asset.uri)) return undefined
  return {
    text: 'Extension',
    tone: 'neutral',
    tooltip: 'continues the previous shot · plays as one sequence in shot details',
  }
}

function getAssetMetadataBadges(asset: Asset): EngineMetadataBadge[] | undefined {
  const badges = [
    getExtensionBadge(asset),
    getFlux3DraftBadge(asset),
    getAssetResolutionBadge(asset),
    ...getAssetHdrBadges(asset),
  ].filter((badge): badge is EngineMetadataBadge => Boolean(badge))
  return badges.length > 0 ? badges : undefined
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export interface EngineThumbnailSelectionOptions {
  /**
   * Current canvas zoom. Node dimensions are world-space CSS pixels, so the
   * effective preview requirement is node dimension × zoom × DPR.
   */
  thumbnailZoom?: number
}

interface ThumbnailDimensions {
  thumbnailWidth: number
  thumbnailHeight: number
}

/**
 * Pixel target for the fallback URL — passed through `getStillImagePreviewTier`
 * which picks the smallest generated tier ≥ this value (tiers: 240, 480, 720,
 * 1080). 240 maps to the smallest 240p tier (~10–20 KB compressed, ~410 KB
 * GPU per texture).
 *
 * Why 240 and not the primary's tier:
 *   - The fallback's job is "show *something* immediately while the primary
 *     loads", not "look pixel-perfect". 240p is enough to recognize the
 *     thumbnail; the primary upgrades it within ~100–200 ms.
 *   - Wide-band prefetch (`prefetchFallbackOnly` in `CanvasEngine`) loads
 *     this URL for hundreds of nodes during a pan. At ~410 KB GPU each
 *     vs ~8 MB for an 'original' tier, the difference is the difference
 *     between OOMing the tab on a dense project and not.
 *
 * Historical note: this was 4096, which `getStillImagePreviewTier` resolved
 * to `'original'` — the source URI for image assets, the 1080 variant for
 * video/audio. Calling that a "fallback" was a misnomer; it was strictly
 * the heaviest texture the cache would ever hold.
 */
const THUMBNAIL_FALLBACK_RENDERED_SIZE = 240
const CANVAS_ORIGINAL_THUMBNAIL_MIN_ZOOM = 4
const CANVAS_ORIGINAL_MAX_TEXTURE_DIMENSION = 8192

function normalizeThumbnailZoom(zoom: number | undefined): number {
  if (typeof zoom !== 'number' || !Number.isFinite(zoom) || zoom <= 0) return 1
  return zoom
}

export function getEngineAssetThumbnailUrl(
  asset: Asset,
  dims: ThumbnailDimensions,
  options: EngineThumbnailSelectionOptions = {},
): string | undefined {
  const zoom = normalizeThumbnailZoom(options.thumbnailZoom)
  const uri = getStillImagePreviewUri(asset, {
    renderedWidth: dims.thumbnailWidth * zoom,
    renderedHeight: dims.thumbnailHeight * zoom,
    allowOriginal: zoom >= CANVAS_ORIGINAL_THUMBNAIL_MIN_ZOOM,
    maxOriginalDimension: CANVAS_ORIGINAL_MAX_TEXTURE_DIMENSION,
  })
  return uri ? getStorageUrl(uri) : undefined
}

export function getEngineAssetThumbnailFallbackUrl(asset: Asset): string | undefined {
  const uri = getStillImagePreviewUri(asset, {
    renderedWidth: THUMBNAIL_FALLBACK_RENDERED_SIZE,
    renderedHeight: THUMBNAIL_FALLBACK_RENDERED_SIZE,
    // Enforce the texture limit here too: without a ladder, a known-oversized
    // image would otherwise return its original as the "fallback" and exceed
    // the WebGPU texture limit.
    maxOriginalDimension: CANVAS_ORIGINAL_MAX_TEXTURE_DIMENSION,
  })
  return uri ? getStorageUrl(uri) : undefined
}

/**
 * Whether this node, at the current zoom, wants a high still-image tier
 * (2160/4096) that the source can provide but hasn't been generated yet.
 * When true the engine asks the React layer to backfill it (see
 * `CanvasEngine.onThumbnailBackfillNeeded`); selection meanwhile shows the best
 * lower tier. Mirrors the backend's `getApplicableStillImageTiers` predicate —
 * they must agree or the backfill loops.
 */
export function getEngineAssetHighResBackfillNeeded(
  asset: Asset,
  dims: ThumbnailDimensions,
  options: EngineThumbnailSelectionOptions = {},
): boolean {
  // Still images only; the high ladder is a still-image concept and these
  // assets always carry width/height. Requires the base ladder to already
  // exist (legacy base-ladder backfill is handled by the React preview hooks).
  if (!isImageAsset(asset) || !hasStillImageThumbnailMetadata(asset)) return false

  const zoom = normalizeThumbnailZoom(options.thumbnailZoom)
  const idealTier = getStillImagePreviewTier({
    renderedWidth: dims.thumbnailWidth * zoom,
    renderedHeight: dims.thumbnailHeight * zoom,
  })

  // Determine the high tier the canvas would actually need.
  let targetTier: StillImageThumbnailTier | undefined
  if (idealTier === 'original') {
    // Zoomed past the 4096 tier. If the original is usable on the canvas
    // (within the texture limit at a high enough zoom), selection shows it and
    // no high tier is needed. Otherwise the original is unusable, so the
    // largest generatable high tier is the sharpest option — target that, or it
    // would silently stay on a lower tier and never get generated.
    const max = Math.max(asset.width ?? 0, asset.height ?? 0)
    const originalUsableOnCanvas =
      zoom >= CANVAS_ORIGINAL_THUMBNAIL_MIN_ZOOM && max > 0 && max <= CANVAS_ORIGINAL_MAX_TEXTURE_DIMENSION
    if (!originalUsableOnCanvas) {
      if (canSourceProvideHighTier(asset, 4096)) targetTier = 4096
      else if (canSourceProvideHighTier(asset, 2160)) targetTier = 2160
    }
  } else if (isHighStillImageTier(idealTier)) {
    targetTier = idealTier
  }

  if (targetTier === undefined) return false
  return canSourceProvideHighTier(asset, targetTier) && !asset.thumbnailVariants?.[targetTier]
}

export function computeAspectRatio(asset: Asset): number {
  // Priority: actual width/height (truth) > aspectRatio string (generation input) > default.
  // `asset.aspectRatio` is the user's *requested* aspect; for some pipelines (Kling V3 i2v,
  // Kling O3 v2v, …) the model infers aspect from the source image/video and the rendered
  // output diverges from the request. Trusting the string over probed dimensions sizes the
  // node to a different shape than the actual video, so the thumbnail and scrubbed frames
  // draw stretched/cropped inside it.
  if (asset.width && asset.height && asset.height > 0) return clampAspectRatio(asset.width / asset.height)
  if (asset.aspectRatio) return parseAspectRatio(asset.aspectRatio)
  return DEFAULT_ASPECT_RATIO
}

export function deriveAssetStatusBadge(asset: Asset): {
  statusText?: string
  statusType?: EngineShotNode['statusType']
  v9DisplayProgress?: number
} {
  if (asset.status === 'uploading') {
    const pct = asset.uploadProgress !== undefined ? ` ${asset.uploadProgress}%` : ''
    return { statusText: `Uploading${pct}`, statusType: 'progress' }
  }
  if (asset.status === 'queued') return { statusText: 'Queued', statusType: 'queued' }
  if (asset.status === 'processing') return { statusText: 'Processing', statusType: 'progress' }
  if (isV9Rendering(asset)) {
    return {
      // V9 render progress is displayed by the unified DOM generation
      // overlay. Leaving statusText/statusType unset prevents the legacy
      // Pixi status badge from rendering a second "Rendering" UI.
      v9DisplayProgress: getV9DisplayProgress(asset),
    }
  }
  if (asset.status === 'failed') {
    const code = asset.error?.code
    if (code === 'CONTENT_POLICY_VIOLATION') return { statusText: 'Content Flagged', statusType: 'failed' }
    // An upload that never reached storage isn't a failed generation; name the fix.
    if (isInterruptedUpload(asset)) return { statusText: 'Re-upload', statusType: 'failed' }
    return { statusText: 'Failed', statusType: 'failed' }
  }
  return {}
}

export function deriveJobStatusBadge(job: Job): {
  statusText?: string
  statusType?: 'progress' | 'failed' | 'queued' | 'info'
} {
  const isGenerating = job.status === 'pending' || job.status === 'running'
  const isFailed = job.status === 'failed'

  if (isFailed) {
    const code = job.error?.code
    if (code === 'CONTENT_POLICY_VIOLATION') return { statusText: 'Content Flagged', statusType: 'failed' }
    return { statusText: 'Failed', statusType: 'failed' }
  }
  if (isGenerating) {
    // DOM overlay (GeneratingAssetOverlayLayer) renders the spinner + % + bar
    // for generating nodes. The Pixi status badge would render behind/beneath
    // the overlay and is barely readable — suppress it.
    return { statusType: 'progress' }
  }
  return {}
}

/** Determine the engine node kind for a job based on its operation. */
export function getJobNodeKind(job: Job): 'shot' | 'image' | 'audio' | 'set' {
  if (job.operation === 'generate-audio' || job.operation === 'inpaint-audio') return 'audio'
  if (job.operation === 'i2s') return 'set'
  if (job.operation === 'generate-image' || job.operation === 'set-screenshot') return 'image'
  return 'shot'
}

// ---------------------------------------------------------------------------
// Shared projection helpers — reduce drift between per-kind builders.
//
// Single source of truth for fields every asset-card node consumes. Adding
// a new shared field (rating, colorLabel, statusText, …) lands here once and
// covers Shot/Image/Audio/Set automatically — without these helpers the
// audio-rating regression that prompted this extraction would recur on every
// new shared field.
// ---------------------------------------------------------------------------

/**
 * Common asset-card fields projected from a completed `Asset`. Per-kind
 * functions spread the result and add their kind-specific extras
 * (`thumbnailUrl`, `aspectRatio`, `mediaType`, `label`, etc.) plus the
 * literal `kind` discriminant.
 *
 * `isGenerating` starts as `false` here and is overridden by per-kind
 * builders for asset-backed flows such as V9 pre-render finalization.
 * Job-side helpers cover normal in-flight generations.
 */
export function buildAssetCommonFields(
  asset: Asset,
  position: CanvasPosition | undefined,
  width: number,
  height: number,
): {
  id: string
  x: number
  y: number
  width: number
  height: number
  parentId: string | undefined
  selected: false
  dimmed: false
  zIndex: number
  asset: Asset
  shotNumber: number | undefined
  colorLabel: string | undefined
  rating: 'favorite' | 'reject' | undefined
  starRating: Asset['starRating']
  statusText: string | undefined
  statusType: 'progress' | 'failed' | 'queued' | 'info' | undefined
  isFailed: boolean
  isDraft: boolean
  isGenerating: false
  generationProgress: undefined
  metadataBadges: EngineMetadataBadge[] | undefined
} {
  const { statusText, statusType } = deriveAssetStatusBadge(asset)
  return {
    id: `asset-${asset.id}`,
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width,
    height,
    parentId: asset.binId ? `bin-${asset.binId}` : undefined,
    selected: false,
    dimmed: false,
    zIndex: 1,
    asset,
    shotNumber: asset.shotNumber,
    colorLabel: asset.colorLabel,
    rating: asset.rating,
    starRating: asset.starRating,
    statusText,
    statusType,
    isFailed: asset.status === 'failed',
    isDraft: asset.status === 'pending' && !asset.uri,
    isGenerating: false,
    generationProgress: undefined,
    metadataBadges: getAssetMetadataBadges(asset),
  }
}

/**
 * Common asset-card fields projected from an in-flight `Job`. Per-kind
 * functions spread + add kind-specific extras. Mirrors
 * `buildAssetCommonFields` but reads job state (progress, status badge,
 * binId) instead of asset state.
 */
export function buildJobCommonFields(
  job: Job,
  position: CanvasPosition | undefined,
  width: number,
  height: number,
): {
  id: string
  x: number
  y: number
  width: number
  height: number
  parentId: string | undefined
  selected: false
  dimmed: false
  zIndex: number
  job: Job
  shotNumber: number | undefined
  variationNumber: number | undefined
  operation: string | undefined
  isGenerating: boolean
  generationProgress: number | undefined
  generationStartedAt: number | undefined
  generationEstimatedSeconds: number | undefined
  isFailed: boolean
  statusText: string | undefined
  statusType: 'progress' | 'failed' | 'queued' | 'info' | undefined
} {
  const { statusText, statusType } = deriveJobStatusBadge(job)
  return {
    id: `job-${job.id}`,
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width,
    height,
    parentId: job.binId ? `bin-${job.binId}` : undefined,
    selected: false,
    dimmed: false,
    zIndex: 1,
    job,
    shotNumber: job.shotNumber,
    variationNumber: job.variationNumber,
    operation: job.operation,
    isGenerating: job.status === 'pending' || job.status === 'running',
    generationProgress: (() => {
      if (job.progress !== undefined) return job.progress
      if (job.estimatedTotalSeconds && job.createdAt) {
        const startedMs = parseTimestampMs(job.createdAt)
        if (startedMs !== undefined) return Math.round(calculateProgress(startedMs, job.estimatedTotalSeconds) * 100)
      }
      return undefined
    })(),
    generationStartedAt: parseTimestampMs(job.createdAt),
    generationEstimatedSeconds: job.estimatedTotalSeconds,
    isFailed: job.status === 'failed',
    statusText,
    statusType,
  }
}

// ---------------------------------------------------------------------------
// Asset → Engine*Node
// ---------------------------------------------------------------------------

export function assetToShotNode(
  asset: Asset,
  position: CanvasPosition | undefined,
  options: EngineThumbnailSelectionOptions = {},
): EngineShotNode {
  const aspectRatio = computeAspectRatio(asset)
  const dims = calculateNodeDimensions(aspectRatio)
  const mediaType: 'image' | 'video' = getAssetMediaKind(asset) === 'image' ? 'image' : 'video'
  const cameraMotionFinalFrameUri = getCameraMotionFinalFrameUri(asset)
  const cameraMotionFinalFrameUrl = cameraMotionFinalFrameUri ? getStorageUrl(cameraMotionFinalFrameUri) : undefined
  const thumbnailUrl = cameraMotionFinalFrameUrl ?? getEngineAssetThumbnailUrl(asset, dims, options)
  const thumbnailFallbackUrl = cameraMotionFinalFrameUrl ?? getEngineAssetThumbnailFallbackUrl(asset)
  const v9Rendering = isV9Rendering(asset)
  const v9DisplayProgress = v9Rendering ? getV9DisplayProgress(asset) : undefined
  return {
    ...buildAssetCommonFields(asset, position, dims.width, dims.thumbnailHeight),
    kind: 'shot',
    thumbnailUrl,
    thumbnailFallbackUrl: thumbnailFallbackUrl !== thumbnailUrl ? thumbnailFallbackUrl : undefined,
    needsHighResBackfill: cameraMotionFinalFrameUrl ? false : getEngineAssetHighResBackfillNeeded(asset, dims, options),
    variationNumber: asset.variationNumber,
    operation: asset.operation,
    isGenerating: v9Rendering,
    generationProgress: v9Rendering ? v9DisplayProgress : undefined,
    generationStartedAt: v9Rendering ? getV9GenerationStartedAt(asset) : undefined,
    generationEstimatedSeconds: undefined,
    hasRemoteViewers: false,
    mediaType,
    aspectRatio,
    assetDuration: asset.duration,
  }
}

export function assetToImageNode(
  asset: Asset,
  position: CanvasPosition | undefined,
  options: EngineThumbnailSelectionOptions = {},
): EngineImageNode {
  const aspectRatio = computeAspectRatio(asset)
  const dims = calculateNodeDimensions(aspectRatio)
  const thumbnailUrl = getEngineAssetThumbnailUrl(asset, dims, options)
  const thumbnailFallbackUrl = getEngineAssetThumbnailFallbackUrl(asset)
  const v9Rendering = isV9Rendering(asset)
  const v9DisplayProgress = v9Rendering ? getV9DisplayProgress(asset) : undefined
  return {
    ...buildAssetCommonFields(asset, position, dims.width, dims.thumbnailHeight),
    kind: 'image',
    thumbnailUrl,
    thumbnailFallbackUrl: thumbnailFallbackUrl !== thumbnailUrl ? thumbnailFallbackUrl : undefined,
    needsHighResBackfill: getEngineAssetHighResBackfillNeeded(asset, dims, options),
    variationNumber: asset.variationNumber,
    operation: asset.operation,
    // A crop/annotate placeholder renders the source thumbnail under the
    // generating wash + spinner while the server authors the real pixels.
    isGenerating: v9Rendering || !!asset.pendingOperation,
    generationProgress: v9Rendering ? v9DisplayProgress : undefined,
    generationStartedAt: v9Rendering ? getV9GenerationStartedAt(asset) : undefined,
    generationEstimatedSeconds: undefined,
    pendingOperation: asset.pendingOperation,
    hasRemoteViewers: false,
    aspectRatio,
  }
}

export function assetToAudioNode(asset: Asset, position: CanvasPosition | undefined): EngineAudioNode {
  return {
    ...buildAssetCommonFields(asset, position, AUDIO_NODE_WIDTH, AUDIO_CONTENT_HEIGHT),
    kind: 'audio',
    label: asset.originalFilename || asset.prompt || undefined,
    waveformUrl: asset.waveformUri ? getStorageUrl(asset.waveformUri) : undefined,
  }
}

export function assetToSetNode(
  asset: Asset,
  position: CanvasPosition | undefined,
  options: EngineThumbnailSelectionOptions = {},
): EngineSetNode {
  // Sets have a split-source-of-truth for `isDraft`: `useSynthesizedNodeReconciliation`
  // owns the true value (factors in splatUrl + V7 generating state, both of which
  // are unavailable here). Omitting `isDraft` from this projection prevents the
  // main reconciliation pass from clobbering the synthesized value when it merges
  // updates into engine state — without this, post-drag (or any positional Yjs
  // commit) would flip `isDraft` to `asset.status === 'pending' && !asset.uri`,
  // which is `false` for completed-pending-splat sets, and the `SetDraftPreviewBody`
  // DOM card would unmount mid-interaction.
  const { isDraft: _omit, ...common } = buildAssetCommonFields(
    asset,
    position,
    SET_NODE_WIDTH,
    Math.round(SET_THUMBNAIL_HEIGHT),
  )
  void _omit
  const thumbnailDims = { thumbnailWidth: SET_NODE_WIDTH, thumbnailHeight: SET_THUMBNAIL_HEIGHT }
  const thumbnailUrl = getEngineAssetThumbnailUrl(asset, thumbnailDims, options)
  const thumbnailFallbackUrl = getEngineAssetThumbnailFallbackUrl(asset)
  return {
    ...common,
    kind: 'set',
    // Name is the user-editable set name from Yjs `setNames` when present
    // (plumbed in `useCanvasEngine`'s sync loop); `asset.prompt` is only a
    // last-resort fallback before the shotNumber fallback inside
    // `SetNodeView._updateName`.
    name: asset.prompt || undefined,
    thumbnailUrl,
    thumbnailFallbackUrl: thumbnailFallbackUrl !== thumbnailUrl ? thumbnailFallbackUrl : undefined,
  }
}

// ---------------------------------------------------------------------------
// Job → Engine*Node (routed by getJobNodeKind)
// ---------------------------------------------------------------------------

export function jobToShotNode(job: Job, position: CanvasPosition | undefined): EngineShotNode {
  const aspectRatio = job.generationParams?.aspectRatio
    ? parseAspectRatio(job.generationParams.aspectRatio)
    : DEFAULT_ASPECT_RATIO
  const dims = calculateNodeDimensions(aspectRatio)
  return {
    ...buildJobCommonFields(job, position, dims.width, dims.thumbnailHeight),
    kind: 'shot',
    hasRemoteViewers: false,
    aspectRatio,
  }
}

export function jobToImageNode(job: Job, position: CanvasPosition | undefined): EngineImageNode {
  const aspectRatio = job.generationParams?.aspectRatio
    ? parseAspectRatio(job.generationParams.aspectRatio)
    : DEFAULT_ASPECT_RATIO
  const dims = calculateNodeDimensions(aspectRatio)
  return {
    ...buildJobCommonFields(job, position, dims.width, dims.thumbnailHeight),
    kind: 'image',
    hasRemoteViewers: false,
    aspectRatio,
  }
}

export function jobToAudioNode(job: Job, position: CanvasPosition | undefined): EngineAudioNode {
  return {
    ...buildJobCommonFields(job, position, AUDIO_NODE_WIDTH, AUDIO_CONTENT_HEIGHT),
    kind: 'audio',
    label: job.generationParams?.prompt || undefined,
  }
}

export function jobToSetNode(job: Job, position: CanvasPosition | undefined): EngineSetNode {
  return {
    ...buildJobCommonFields(job, position, SET_NODE_WIDTH, Math.round(SET_THUMBNAIL_HEIGHT)),
    kind: 'set',
    name: job.generationParams?.prompt || undefined,
  }
}

// ---------------------------------------------------------------------------
// Bin → EngineBinGroupNode
// ---------------------------------------------------------------------------

export function binToGroupNode(
  bin: CanvasBin,
  position: CanvasPosition | undefined,
  childCount: number,
  depth: number,
  previewItems?: BinPreviewItem[],
): EngineBinGroupNode {
  const x = position?.x ?? 0
  const y = position?.y ?? 0

  return {
    id: `bin-${bin.id}`,
    kind: 'binGroup',
    x,
    y,
    width: position?.width ?? DEFAULT_BIN_WIDTH,
    height: position?.height ?? DEFAULT_BIN_HEIGHT,
    parentId: bin.parentBinId ? `bin-${bin.parentBinId}` : undefined,
    selected: false,
    dimmed: false,
    zIndex: 0, // bins render below nodes
    bin,
    color: bin.color,
    starRating: bin.starRating,
    colorLabel: bin.colorLabel,
    isCollapsed: false,
    childCount,
    depth,
    isDropTarget: false,
    previewItems,
  }
}

// ---------------------------------------------------------------------------
// Other canvas nodes
// ---------------------------------------------------------------------------

export function annotationToEngineNode(
  annotation: TextAnnotation,
  position: CanvasPosition | undefined,
  plainText: string | undefined,
): EngineTextAnnotationNode {
  return {
    id: `annotation-${annotation.id}`,
    kind: 'textAnnotation',
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: annotation.width ?? 200,
    // A note's height is content-driven and measured by TextAnnotationDomLayer,
    // which mirrors the measurement back into the canvas position. Seed from it
    // so a long note is known-tall on first load and after a full resync — the
    // DOM layer's viewport culling reads this height, and starting every note at
    // the minimum made tall ones cull themselves off-screen. ENG-713.
    height: Math.max(TEXT_ANNOTATION_MIN_HEIGHT, position?.height ?? 0),
    parentId: annotation.binId ? `bin-${annotation.binId}` : undefined,
    selected: false,
    dimmed: false,
    zIndex: 2,
    annotation,
    plainText,
  }
}

export function subjectToEngineNode(
  subject: SubjectCanvasNode,
  position: CanvasPosition | undefined,
): EngineSubjectNode {
  return {
    id: `subject-${subject.id}`,
    kind: 'subject',
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: 113,
    height: 113,
    parentId: subject.binId ? `bin-${subject.binId}` : undefined,
    selected: false,
    dimmed: false,
    zIndex: 1,
    subjectId: subject.id,
    name: subject.name,
    category: subject.category,
    thumbnailUrl: subject.thumbnailUrl ?? subject.mainImageUrl,
    color: subject.color,
    colorLabel: subject.colorLabel,
    shotNumber: subject.shotNumber,
    rating: subject.rating,
    starRating: subject.starRating,
    stackId: subject.stackId,
    isGenerating: isPlaceholderSubjectId(subject.id),
  }
}

export function subjectCollectionToEngineNode(
  collection: SubjectCollection,
  subjects: ReadonlyMap<string, SubjectCanvasNode>,
  position: CanvasPosition | undefined,
): EngineSubjectCollectionNode {
  const variants = collection.memberOrder.flatMap(member => {
    const subjectId = member.subjectId
    const subject = subjects.get(subjectId)
    if (!subject) return []
    const thumbnailUrl = subject.thumbnailUrl ?? subject.mainImageUrl
    return [
      {
        subjectId,
        name: subject.name,
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(subject.shotNumber !== undefined ? { shotNumber: subject.shotNumber } : {}),
        ...(subject.starRating !== undefined ? { starRating: subject.starRating } : {}),
      },
    ]
  })

  // `width` / `height` from CanvasPosition are stored in raw pixels (same
  // convention as bins / variables); `resolveCollectionLayout` snaps them
  // to the nearest integer cols/rows boundary and re-derives the actual
  // pixel size the view should paint at.
  const layout = resolveCollectionLayout({
    variantCount: variants.length,
    persistedWidth: position?.width,
    persistedHeight: position?.height,
  })

  return {
    id: `collection-${collection.id}`,
    kind: 'subjectCollection',
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: layout.width,
    height: layout.height,
    parentId: position?.parentBinId ? `bin-${position.parentBinId}` : undefined,
    selected: false,
    dimmed: false,
    zIndex: 1,
    collectionId: collection.id,
    collection,
    name: collection.name,
    variants,
    columns: layout.columns,
    rows: layout.rows,
  }
}

export function variableToEngineNode(
  variable: VariableCanvasNode,
  position: CanvasPosition | undefined,
): EngineVariableNode {
  return {
    id: `variable-${variable.id}`,
    kind: 'variable',
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: position?.width ?? 180,
    height: 56,
    parentId: variable.binId ? `bin-${variable.binId}` : undefined,
    selected: false,
    dimmed: false,
    zIndex: 1,
    variableId: variable.id,
    name: variable.name,
    value: variable.value,
    color: variable.color,
    colorLabel: variable.colorLabel,
    rating: variable.rating,
    starRating: variable.starRating,
  }
}

export const RUN_ALIVE_STATUSES: ReadonlySet<WorkflowRunStatus['status']> = new Set([
  'pending',
  'running',
  'paused',
  'awaiting_approval',
])

export function actionToEngineNode(
  action: Action,
  runStatus: WorkflowRunStatus | undefined,
  position: CanvasPosition | undefined,
  stepNumber: number,
  missingInputKeys: string[] = [],
): EngineActionNode {
  const base: EngineActionNode = {
    id: `action-${action.id}`,
    kind: 'action',
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: 272,
    height: 118,
    selected: false,
    dimmed: false,
    zIndex: 1,
    actionId: action.id,
    action,
    stepNumber,
    completedItemCount: 0,
    totalItemCount: 0,
    failedItemCount: 0,
    missingInputKeys,
  }
  // A build (compile) run executes no step, so it is the workflow's state, not each card's: the
  // frame band carries the spinner and the clock while it runs (six cards spinning read as six
  // steps running in parallel with nothing landing), and nothing once the scripts are prepared —
  // the chained run takes over. Only a failed build marks the cards, so the failure stays on the
  // canvas after the band's live label is gone.
  if (runStatus?.build) {
    if (!mirrorStands(runStatus)) return base
    const buildStatus = buildActionStatus(runStatus)
    return buildStatus === 'build_failed' ? { ...base, runStatus: buildStatus } : base
  }
  const isCurrentAction = runStatus?.currentActionId === action.id
  // While the run is parked at the gate no items exist yet, so the card's
  // counter reads the approved plan's total instead of an empty item list.
  const plannedTotal =
    isCurrentAction && runStatus.totalItemCount === 0 ? (runStatus.plan?.totalPlannedOutputs ?? 0) : 0
  // Not the current action: the mirror's per-action slot says whether it is
  // still queued ("waiting") or already finished. Queued only reads as such
  // while the run is alive — a cancelled run leaves no ghost "waiting" cards,
  // and only real outcomes (done / failed) outlive the run.
  const slot =
    !isCurrentAction && runStatus ? runStatus.actions?.find(entry => entry.actionId === action.id) : undefined
  const runAlive = runStatus ? RUN_ALIVE_STATUSES.has(runStatus.status) : false
  // The card's clock: this step's own start when the mirror has it, else the run's.
  const currentSlot = isCurrentAction ? runStatus.actions?.find(entry => entry.actionId === action.id) : undefined
  const startedAt = isCurrentAction ? Date.parse(currentSlot?.startedAt ?? runStatus.startedAt ?? '') : NaN
  const slotStatus = slot
    ? runAlive
      ? slot.status
      : slot.status === 'completed' || slot.status === 'failed'
        ? slot.status
        : undefined
    : undefined
  return {
    ...base,
    // The run's index stays on a finished action while the engine settles; its own completion wins.
    runStatus: isCurrentAction ? (currentSlot?.status === 'completed' ? 'completed' : runStatus.status) : slotStatus,
    runStartedAt: Number.isFinite(startedAt) ? startedAt : undefined,
    completedItemCount: isCurrentAction ? runStatus.completedItemCount : 0,
    totalItemCount: isCurrentAction ? runStatus.totalItemCount || plannedTotal : 0,
    failedItemCount: isCurrentAction ? runStatus.failedItemCount : 0,
  }
}

export function assetToFileNode(asset: Asset, position: CanvasPosition | undefined): EngineFileNode {
  return {
    ...buildAssetCommonFields(asset, position, 240, 80),
    kind: 'file',
    asset,
    label: asset.originalFilename ?? 'JSON file',
    isGenerating: false,
  }
}
